import { useI18n } from "@excalidraw/excalidraw/i18n";
import {
  useExcalidrawSetAppState,
  useExcalidrawActionManager,
  useExcalidrawElements,
  useAppProps,
} from "@excalidraw/excalidraw/components/App";
import {
  actionClearCanvas,
  actionLoadScene,
  actionSaveToActiveFile,
  actionShortcuts,
  actionToggleSearchMenu,
  actionToggleTheme,
} from "@excalidraw/excalidraw/actions";
import { getShortcutFromShortcutName } from "@excalidraw/excalidraw/actions/shortcuts";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import Trans from "@excalidraw/excalidraw/components/Trans";
import DropdownMenuItem from "@excalidraw/excalidraw/components/dropdownMenu/DropdownMenuItem";
import DropdownMenuItemContentRadio from "@excalidraw/excalidraw/components/dropdownMenu/DropdownMenuItemContentRadio";

// TODO: Define new icons for the new items with using Lucide-react icons
import {
  boltIcon,
  DeviceDesktopIcon,
  ExportIcon,
  ExportImageIcon,
  HelpIcon,
  LoadIcon,
  MoonIcon,
  save,
  searchIcon,
  SunIcon,
  TrashIcon,
  usersIcon,
  SaveIcon,
  enableSaveAutomaticallyIcon,
  disableSaveAutomaticallyIcon,
} from "@excalidraw/excalidraw/components/icons";
import { Save } from "lucide-react";

export const LoadScene = () => {
  const { t } = useI18n();
  const actionManager = useExcalidrawActionManager();
  const elements = useExcalidrawElements();

  if (!actionManager.isActionEnabled(actionLoadScene)) {
    return null;
  }

  const handleSelect = async () => {
    if (
      !elements.length ||
      (await openConfirmModal({
        title: t("overwriteConfirm.modal.loadFromFile.title"),
        actionLabel: t("overwriteConfirm.modal.loadFromFile.button"),
        color: "warning",
        description: (
          <Trans
            i18nKey="overwriteConfirm.modal.loadFromFile.description"
            bold={(text) => <strong>{text}</strong>}
            br={() => <br />}
          />
        ),
      }))
    ) {
      actionManager.executeAction(actionLoadScene);
    }
  };

  return (
    <DropdownMenuItem
      icon={LoadIcon}
      onSelect={handleSelect}
      data-testid="load-button"
      shortcut={getShortcutFromShortcutName("loadScene")}
      aria-label={t("buttons.load")}
    >
      {t("buttons.load")}
    </DropdownMenuItem>
  );
};
LoadScene.displayName = "LoadScene";

export const ToggleAutoSave = (props: {
  autoSaveEnabled: boolean;
  setAutoSaveEnabled: (enabled: boolean) => void;
}) => {
  const { t } = useI18n();

  return (
    <DropdownMenuItemContentRadio
      name="theme"
      value={props.autoSaveEnabled ? "enabled" : "disabled"}
      onChange={(value: string) =>
        props.setAutoSaveEnabled(value === "enabled")
      }
      choices={[
        {
          value: "enabled",
          label: enableSaveAutomaticallyIcon,
          // ariaLabel: `${t("buttons.lightMode")} - ${shortcut}`,
        },
        {
          value: "disabled",
          label: disableSaveAutomaticallyIcon,
          // ariaLabel: `${t("buttons.darkMode")} - ${shortcut}`,
        },
      ]}
    >
      {t("buttons.toggleAutoSave")}
    </DropdownMenuItemContentRadio>
  );
};
ToggleAutoSave.displayName = "ToggleAutoSave";

export const SaveToLibrary = (props: { onSelect: () => void }) => {
  const { t } = useI18n();

  return (
    <DropdownMenuItem
      icon={SaveIcon}
      onSelect={props.onSelect}
      data-testid="save-as-new-diagram-button"
      aria-label={t("buttons.saveToLibrary")}
    >
      {t("buttons.saveToLibrary")}
    </DropdownMenuItem>
  );
};
SaveToLibrary.displayName = "SaveToLibrary";

export const SaveAsNewDiagram = (props: { onSelect: () => void }) => {
  const { t } = useI18n();

  return (
    <DropdownMenuItem
      icon={SaveIcon}
      onSelect={props.onSelect}
      data-testid="save-as-new-diagram-button"
      aria-label={t("buttons.saveAsNewDiagram")}
    >
      {t("buttons.saveAsNewDiagram")}
    </DropdownMenuItem>
  );
};
SaveAsNewDiagram.displayName = "SaveAsNewDiagram";

export const SaveAsNewTemplate = (props: { onSelect: () => void }) => {
  const { t } = useI18n();
  return (
    <DropdownMenuItem
      icon={SaveIcon}
      onSelect={props.onSelect}
      data-testid="save-as-new-template-button"
      aria-label={t("buttons.saveAsNewTemplate")}
    >
      {t("buttons.saveAsNewTemplate")}
    </DropdownMenuItem>
  );
};
SaveAsNewTemplate.displayName = "SaveAsNewTemplate";
